import { Observable, Subject, of } from 'rxjs';
import { throwError } from 'rxjs';
import { takeUntil, switchMap, map, catchError } from 'rxjs/operators';

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort, SortDirection } from '@angular/material/sort';

import { ISnListQueryParameters, ISnListResponse, SortParameters } from '@lib/api';
import { MfaBackup, UserMfaDevice } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';
import { SnActionConfig } from '@lib/ui/actions';
import { SnDataListComponent } from '@lib/ui/data-list';

import { ManageMfaDialogComponent } from '../manage-mfa-dialog';
import { MfaBackupCodesDialogComponent } from '../mfa-backup-codes-dialog/mfa-backup-codes-dialog.component';
import { MfaVerificationDialogComponent } from '../mfa-verification-dialog/mfa-verification-dialog.component';
import { TwoFactorAuthenticationDialogComponent } from '../two-factor-authentication-dialog';

@Component({
  selector: 'sn-mfa-list-dialog',
  templateUrl: './mfa-list-dialog.component.html',
  styleUrls: ['./mfa-list-dialog.component.scss'],
})
export class MfaListDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: true })
  public readonly list: SnDataListComponent<UserMfaDevice>;

  public readonly columns: string[] = this._genColumns();
  public readonly actions: SnActionConfig<UserMfaDevice>[] = this._genActions();

  public readonly loadData = this._loadData.bind(this);

  public sort: string = 'name';
  public sortDirection: SortDirection = 'asc';
  public backupCodes: MfaBackup;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _usersAPI: SnUsersAPI,
    private readonly _currentUser: SnCurrentUser,
    private readonly _dialog: MatDialog,
  ) {}

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSortChange(sort: Sort): void {
    this.sort = sort.active;
    this.sortDirection = sort.direction;

    this.reloadData();
  }

  public addAccount(): void {
    this._dialog
      .open(TwoFactorAuthenticationDialogComponent, {
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.list.dataSource.insertItemToStart(result);
      });
  }

  public seeBackupCodes(): void {
    this._dialog.open(MfaBackupCodesDialogComponent, {
      data: this._currentUser,
    });
  }

  public reloadData(): void {
    this.list.reference.reload();
  }

  private _manageMfa(mfa: UserMfaDevice, action: string): void {
    this._dialog
      .open(ManageMfaDialogComponent, {
        data: {
          user: this._currentUser,
          mfa,
          action,
        },
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        if (action === 'deactivate') {
          this.list.dataSource.deleteItem(result);
        } else {
          this.list.dataSource.updateItem(result);
        }
      });
  }

  private _deleteMfa(mfa: UserMfaDevice): void {
    this._showMfaPasswordDialog(mfa);
  }

  private _showMfaPasswordDialog(mfa: UserMfaDevice, errorMessage?: string): void {
    const dialogRef = this._dialog.open(MfaVerificationDialogComponent, {
      data: { mfa, errorMessage },
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntil(this._destroy$),
        switchMap((result) => {
          if (!result || !result.confirmed) {
            return of(null);
          }

          return this._usersAPI
            .deleteMfaWithPassword(this._currentUser.id, mfa.id, result.password)
            .pipe(
              map(() => true),
              catchError((error) => {
                const errorMsg = error.error?.detail || 'An error occurred. Please try again.';

                this._showMfaPasswordDialog(mfa, errorMsg);
                return of(null);
              }),
            );
        }),
      )
      .subscribe({
        next: (success) => {
          if (success === true) {
            this.list.dataSource.deleteItem(mfa);
          }
        },
        error: (error) => {
          console.error('Unexpected error deleting MFA device:', error);
          dialogRef.close();
        },
      });
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<UserMfaDevice>> {
    return this._usersAPI
      .getMfaDeviceList(this._currentUser.id, this._prepareLoadQueryParameters(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _prepareLoadQueryParameters<T>(params: T): T {
    return {
      ordering: new SortParameters({
        active: this.sort,
        direction: this.sortDirection,
      }).convertedActive,
      ...params,
    };
  }

  private _genColumns(): string[] {
    return ['name', 'confirmed', 'actions'];
  }

  private _genActions(): SnActionConfig<UserMfaDevice>[] {
    return [
      {
        label: 'common.activate',
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-circle-check',
        },
        click: (mfa) => this._manageMfa(mfa, 'activate'),
        hidden: (mfa) => mfa.confirmed,
      },
      {
        label: 'common.deactivate',
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-ban',
        },
        click: (mfa) => this._manageMfa(mfa, 'deactivate'),
        hidden: (item) => !item.confirmed,
      },
      {
        label: 'common.delete',
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-trash-alt',
        },
        color: 'warn',
        click: (mfa) => this._deleteMfa(mfa),
      },
    ];
  }
}
